import { useState, useEffect, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { formatRelativeTime, formatDateTime } from '../lib/utils';
import { Activity, AlertTriangle, RefreshCw, Terminal } from 'lucide-react';

type MatchedFilter = 'all' | 'matched' | 'unmatched';

interface PostbackLog {
  id: number;
  clickId: string | null;
  offerId: number | null;
  method: string;
  path: string;
  queryString: string | null;
  headers: string | null;
  rawBody: string | null;
  sourceIp: string | null;
  userAgent: string | null;
  signatureValid: boolean | null;
  matched: boolean;
  rejectedReason: string | null;
  finalStatus: string | null;
  createdAt: number;
}

interface PostbackLogsResponse {
  data: PostbackLog[];
  meta: { total: number; limit: number; offset: number };
}

function prettyJson(raw: string | null): string {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function parseParams(log: PostbackLog): Record<string, string> {
  const params: Record<string, string> = {};
  if (log.queryString) {
    for (const [key, value] of new URLSearchParams(log.queryString).entries()) {
      params[key] = value;
    }
  }
  if (log.rawBody) {
    try {
      const parsed = JSON.parse(log.rawBody) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        for (const [key, value] of Object.entries(
          parsed as Record<string, unknown>,
        )) {
          if (value != null) params[key] = String(value);
        }
      }
    } catch {
      for (const [key, value] of new URLSearchParams(log.rawBody).entries()) {
        params[key] = value;
      }
    }
  }
  return params;
}

function firstParam(
  params: Record<string, string>,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = params[key];
    if (value) return value;
  }
  return undefined;
}

function payloadSummary(log: PostbackLog): string {
  const params = parseParams(log);
  const parts = [
    firstParam(params, ['revenue', 'payout', 'r'])
      ? `rev ${firstParam(params, ['revenue', 'payout', 'r'])}`
      : '',
    firstParam(params, ['status', 's'])
      ? `status ${firstParam(params, ['status', 's'])}`
      : '',
    firstParam(params, ['sub_id', 'subid', 'aff_sub', 's1'])
      ? `sub ${firstParam(params, ['sub_id', 'subid', 'aff_sub', 's1'])}`
      : '',
    log.finalStatus ? `final ${log.finalStatus}` : '',
  ].filter(Boolean);
  return parts.join(' · ');
}

function MethodBadge({ method }: { method: string }) {
  const isPost = method === 'POST';
  return (
    <span
      className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded border ${
        isPost
          ? 'border-neon-magenta text-neon-magenta bg-neon-magenta/10'
          : 'border-neon-cyan text-neon-cyan bg-neon-cyan/10'
      }`}
    >
      {method}
    </span>
  );
}

function StatusCell({ log }: { log: PostbackLog }) {
  if (log.matched && log.signatureValid !== false) {
    return <Badge variant="live">MATCHED</Badge>;
  }
  if (log.signatureValid === false) {
    return <Badge variant="error">INVALID SIG</Badge>;
  }
  // unmatched
  const reason = log.rejectedReason ? ` (${log.rejectedReason})` : '';
  return (
    <span className="font-mono text-[10px] text-neon-red">
      UNMATCHED{reason}
    </span>
  );
}

function ExpandedDetail({ log }: { log: PostbackLog }) {
  const fullPath = log.queryString
    ? `${log.path}?${log.queryString}`
    : log.path;
  return (
    <tr>
      <td
        colSpan={8}
        className="bg-bg-primary border-b border-border-subtle p-0"
      >
        <div className="px-4 py-3 space-y-3 animate-fade-in">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 text-xs font-mono">
            <div>
              <span className="text-text-secondary">Log ID: </span>
              <span className="text-neon-cyan">#{log.id}</span>
            </div>
            <div>
              <span className="text-text-secondary">Offer: </span>
              <span className="text-neon-yellow">
                {log.offerId != null ? `#${log.offerId}` : '-'}
              </span>
            </div>
            <div>
              <span className="text-text-secondary">Matched: </span>
              <span
                className={log.matched ? 'text-neon-green' : 'text-neon-red'}
              >
                {log.matched ? 'yes' : 'no'}
              </span>
            </div>
            <div>
              <span className="text-text-secondary">Final: </span>
              <span className="text-neon-magenta">
                {log.finalStatus ?? '-'}
              </span>
            </div>
            <div>
              <span className="text-text-secondary">Exact Time: </span>
              <span className="text-text-primary">
                {formatDateTime(log.createdAt)}
              </span>
            </div>
          </div>

          {/* Path */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-text-secondary font-display mb-1">
              Full Request Path
            </div>
            <code className="font-mono text-xs text-neon-cyan break-all">
              {fullPath}
            </code>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Headers */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-text-secondary font-display mb-1">
                Headers
              </div>
              <pre className="terminal whitespace-pre-wrap break-all overflow-auto max-h-48 text-[11px]">
                {prettyJson(log.headers) || '// no headers captured'}
              </pre>
            </div>

            {/* Body */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-text-secondary font-display mb-1">
                Raw Body
              </div>
              <pre className="terminal whitespace-pre-wrap break-all overflow-auto max-h-48 text-[11px]">
                {prettyJson(log.rawBody) || '// no body'}
              </pre>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-1.5 text-xs font-mono">
            <div>
              <span className="text-text-secondary">Source IP: </span>
              <span className="text-neon-cyan">{log.sourceIp ?? '-'}</span>
            </div>
            <div>
              <span className="text-text-secondary">Sig Valid: </span>
              <span
                className={
                  log.signatureValid === true
                    ? 'text-neon-green'
                    : log.signatureValid === false
                      ? 'text-neon-red'
                      : 'text-text-tertiary'
                }
              >
                {log.signatureValid === null
                  ? 'n/a'
                  : log.signatureValid
                    ? 'yes'
                    : 'no'}
              </span>
            </div>
            <div>
              <span className="text-text-secondary">Final Status: </span>
              <span className="text-neon-yellow">{log.finalStatus ?? '-'}</span>
            </div>
            {log.rejectedReason && (
              <div>
                <span className="text-text-secondary">Rejected: </span>
                <span className="text-neon-red">{log.rejectedReason}</span>
              </div>
            )}
          </div>

          {log.userAgent && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-text-secondary font-display mb-1">
                User Agent
              </div>
              <span className="font-mono text-[10px] text-text-tertiary break-all">
                {log.userAgent}
              </span>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

export function PostbackLogsPage() {
  const [filter, setFilter] = useState<MatchedFilter>('all');
  const [offset, setOffset] = useState(0);
  const [allLogs, setAllLogs] = useState<PostbackLog[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const query = useQuery({
    queryKey: ['postback-logs', filter, offset],
    queryFn: () =>
      apiClient.getEnvelope<PostbackLog[]>('/api/v1/postback-logs', {
        ...(filter !== 'all'
          ? { matched: filter === 'matched' ? 'true' : 'false' }
          : {}),
        limit: 100,
        offset,
      }),
    // One polling mechanism only. Global QueryClient config pauses this when
    // the tab is hidden, so LIVE mode does not keep spending requests in the
    // background.
    refetchInterval: autoRefresh ? 5000 : false,
    placeholderData: (prev) => prev, // keep showing previous data during refetch — no empty flash
  });

  // Accumulate later pages into allLogs only — first page renders directly
  // from query.data.data so there's no empty-state flicker on mount.
  useEffect(() => {
    if (!query.data?.data) return;
    if (offset > 0) {
      setAllLogs((prev) => {
        const existingIds = new Set(prev.map((l) => l.id));
        const newItems = query.data!.data.filter((l) => !existingIds.has(l.id));
        return [...prev, ...newItems];
      });
    }
  }, [query.data, offset]);

  // Reset accumulation when filter changes
  useEffect(() => {
    setOffset(0);
    setAllLogs([]);
    setExpandedId(null);
  }, [filter]);

  // Display: page-0 reads directly from query.data (no flash); paginated views
  // merge accumulator with current page.
  const displayedLogs: PostbackLog[] =
    offset === 0
      ? (query.data?.data ?? [])
      : [
          ...allLogs,
          ...(query.data?.data ?? []).filter(
            (l) => !allLogs.some((p) => p.id === l.id),
          ),
        ];
  const unmatchedCount = displayedLogs.filter((l) => !l.matched).length;
  const total = query.data?.meta?.total ?? 0;
  const hasMore = displayedLogs.length < total;

  const toggleRow = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="w-full space-y-5 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest flex items-center gap-2">
            <Terminal size={22} className="text-neon-cyan" />
            POSTBACK LOGS
          </h1>
          <p className="text-sm text-text-secondary font-mono mt-1">
            // debug incoming postbacks from networks
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            title={
              autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh (5s)'
            }
            className={`flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded border transition-all ${
              autoRefresh
                ? 'border-neon-green text-neon-green bg-neon-green/10'
                : 'border-border-subtle text-text-secondary hover:text-text-primary hover:border-text-secondary'
            }`}
          >
            <RefreshCw
              size={12}
              className={autoRefresh ? 'animate-spin' : ''}
            />
            {autoRefresh ? 'LIVE' : 'REFRESH OFF'}
          </button>
          <Button
            variant="ghost"
            size="sm"
            icon={<RefreshCw size={12} />}
            onClick={() => {
              setOffset(0);
              // D6: Don't clear allLogs — placeholderData keeps previous entries visible
              // during the in-flight refetch, preventing an empty-state flash.
              query.refetch();
            }}
            loading={query.isFetching}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Unmatched alert */}
      {unmatchedCount > 0 && (
        <div className="flex items-center gap-2 p-3.5 rounded border border-neon-red/50 bg-neon-red/5 font-mono text-xs text-neon-red">
          <AlertTriangle size={16} className="shrink-0" />
          <span>
            {unmatchedCount} unmatched postback{unmatchedCount !== 1 ? 's' : ''}{' '}
            detected — check rejection reasons below
          </span>
        </div>
      )}

      {/* Filter pills */}
      <div className="flex gap-1 border-b border-border-subtle">
        {(['all', 'matched', 'unmatched'] as MatchedFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-xs font-display uppercase tracking-wider transition-all ${
              filter === f
                ? 'text-neon-cyan border-b-2 border-neon-cyan'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {f}
          </button>
        ))}
        <span className="ml-auto self-center text-xs font-mono text-text-tertiary pr-2">
          {total > 0 ? `${displayedLogs.length} / ${total}` : ''}
        </span>
      </div>

      {/* Table */}
      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Time</th>
                <th>Method</th>
                <th>Path</th>
                <th>Offer</th>
                <th>Click ID</th>
                <th>Detail</th>
                <th>Source IP</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {displayedLogs.map((log) => (
                <Fragment key={log.id}>
                  <tr
                    onClick={() => toggleRow(log.id)}
                    className={`cursor-pointer transition-colors ${
                      expandedId === log.id
                        ? 'bg-bg-secondary'
                        : 'hover:bg-bg-secondary/50'
                    }`}
                  >
                    <td
                      className="text-xs text-text-tertiary font-mono whitespace-nowrap"
                      title={formatDateTime(log.createdAt)}
                    >
                      {formatRelativeTime(log.createdAt)}
                    </td>
                    <td>
                      <MethodBadge method={log.method} />
                    </td>
                    <td>
                      <code className="font-mono text-xs text-text-primary">
                        {log.path}
                      </code>
                    </td>
                    <td className="font-mono text-xs text-neon-yellow">
                      {log.offerId != null ? `#${log.offerId}` : '-'}
                    </td>
                    <td className="font-mono text-xs text-text-secondary">
                      {log.clickId ? (
                        <span title={log.clickId}>
                          {log.clickId.slice(0, 12)}…
                        </span>
                      ) : (
                        <span className="text-text-tertiary">-</span>
                      )}
                    </td>
                    <td className="font-mono text-[11px] text-text-tertiary max-w-xs">
                      <span className="line-clamp-2">
                        {payloadSummary(log) || '-'}
                      </span>
                    </td>
                    <td className="font-mono text-xs text-text-secondary">
                      {log.sourceIp ?? '-'}
                    </td>
                    <td>
                      <StatusCell log={log} />
                    </td>
                  </tr>
                  {expandedId === log.id && <ExpandedDetail log={log} />}
                </Fragment>
              ))}

              {displayedLogs.length === 0 && !query.isLoading && (
                <tr>
                  <td
                    colSpan={8}
                    className="text-center py-16 text-text-tertiary"
                  >
                    <Activity className="mx-auto mb-3 opacity-30" size={36} />
                    <p className="font-mono text-xs">
                      no postbacks received yet — fire one to test
                    </p>
                  </td>
                </tr>
              )}

              {query.isLoading && displayedLogs.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="text-center py-12 text-text-tertiary font-mono text-xs"
                  >
                    loading...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Load more */}
      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOffset((o) => o + 100)}
            loading={query.isFetching}
          >
            Load more ({total - displayedLogs.length} remaining)
          </Button>
        </div>
      )}
    </div>
  );
}
